import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "./ThemeProvider";
import { dialogs } from "../context/DialogContext";
import api from "../services/api";
import { postCaptacionMultipart } from "../services/captacionUpload";
import FichaResumenPropiedad from "./propiedades/FichaResumenPropiedad";
import {
  ensureMensajesInternosAnimCss,
  MSG_INTERNA_ANIM_MS,
  propsDeMensaje,
} from "../utils/mensajesInternosAnim";

const QUICK_MSGS = [
  { id: "llamame", emoji: "📞", label: "Llámame", text: "¿Me puedes llamar cuando puedas?" },
  { id: "en_oficina", emoji: "🏢", label: "En oficina", text: "Estoy en la oficina si me necesitas." },
  { id: "en_visita", emoji: "🚗", label: "En visita", text: "Estoy en visita. Te escribo en cuanto pueda." },
  { id: "llaves", emoji: "🔑", label: "Llaves", text: "¿Tienes las llaves de la propiedad? Las necesito." },
  { id: "agenda", emoji: "📅", label: "Agenda", text: "¿Puedes mirar la agenda y confirmarme disponibilidad?" },
  { id: "propiedad", emoji: "🏠", label: "Propiedad", text: "Sobre la propiedad [ref]: " },
  { id: "ok", emoji: "✅", label: "OK / Hecho", text: "Hecho, todo listo." },
  { id: "ayuda", emoji: "🙋", label: "Necesito ayuda", text: "¿Me puedes echar una mano con esto?" },
];

const ACCENT = {
  from: "#0d9488",
  to: "#14b8a6",
  soft: "rgba(20,184,166,0.12)",
  border: "rgba(20,184,166,0.35)",
  text: "#5eead4",
  bar: "linear-gradient(90deg, #2dd4bf, #14b8a6, #0d9488, #14b8a6, #2dd4bf)",
};

const MAX_ARCHIVOS = 6;
const MAX_MB = 20;

function fmtFecha(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function iconoPorTipo(file) {
  if (file.type?.startsWith("image/")) return null;
  if (file.type === "application/pdf") return "📄";
  return "📎";
}

function formatoTamano(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * @param {{ open: boolean, onClose: () => void, onUnreadChange?: (n:number)=>void, replyTo?: { paraId: string, nombre?: string } | null, onReplyConsumed?: () => void }} props
 */
export default function ModalComunicacionInterna({
  open,
  onClose,
  onUnreadChange,
  replyTo = null,
  onReplyConsumed,
}) {
  const { theme } = useTheme();
  const t = theme.colors;
  const isDark = theme.name === "dark";

  const [tab, setTab] = useState("bandeja");
  const [caja, setCaja] = useState("recibidos");
  const [mensajes, setMensajes] = useState([]);
  const [destinatarios, setDestinatarios] = useState([]);
  const [paraIds, setParaIds] = useState([]);
  const [filtroDest, setFiltroDest] = useState("");
  const [cuerpo, setCuerpo] = useState("");
  const [plantillaId, setPlantillaId] = useState(null);
  const [archivos, setArchivos] = useState([]);
  const [propiedadesSel, setPropiedadesSel] = useState([]);
  const [busquedaProp, setBusquedaProp] = useState("");
  const [resultadosProp, setResultadosProp] = useState([]);
  const [buscandoProp, setBuscandoProp] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [enviadoA, setEnviadoA] = useState(1);
  const [seleccionado, setSeleccionado] = useState(null);
  const [vistaPreviaId, setVistaPreviaId] = useState(null);
  const [mounted, setMounted] = useState(open);
  const [animOut, setAnimOut] = useState(false);
  const textareaRef = useRef(null);
  const inputFileRef = useRef(null);

  useEffect(() => {
    ensureMensajesInternosAnimCss();
  }, []);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setAnimOut(false);
      return undefined;
    }
    setAnimOut(true);
    const id = window.setTimeout(() => setMounted(false), MSG_INTERNA_ANIM_MS);
    return () => window.clearTimeout(id);
  }, [open]);
  const debouncePropRef = useRef(null);

  const cargarBandeja = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get("/mensajes-internos", { params: { caja } });
      setMensajes(data?.data || []);
    } catch (e) {
      console.error("[ModalComunicacionInterna] bandeja", e);
    } finally {
      setCargando(false);
    }
  }, [caja]);

  const cargarDestinatarios = useCallback(async () => {
    try {
      const { data } = await api.get("/mensajes-internos/destinatarios");
      setDestinatarios(data?.data || []);
    } catch (e) {
      console.error("[ModalComunicacionInterna] destinatarios", e);
    }
  }, []);

  const refrescarNoLeidos = useCallback(async () => {
    try {
      const { data } = await api.get("/mensajes-internos/no-leidos");
      onUnreadChange?.(data?.total || 0);
    } catch {
      /* silencioso */
    }
  }, [onUnreadChange]);

  useEffect(() => {
    if (!open) return;
    setEnviado(false);
    if (tab === "bandeja") cargarBandeja();
    if (tab === "nuevo") cargarDestinatarios();
  }, [open, tab, caja, cargarBandeja, cargarDestinatarios]);

  useEffect(() => {
    if (open) return undefined;
    setSeleccionado(null);
    setCuerpo("");
    setParaIds([]);
    setFiltroDest("");
    setPlantillaId(null);
    setArchivos((prev) => {
      prev.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
      return [];
    });
    setPropiedadesSel([]);
    setBusquedaProp("");
    setResultadosProp([]);
    setTab("bandeja");
    setCaja("recibidos");
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open || !replyTo?.paraId) return;
    setTab("nuevo");
    setParaIds([replyTo.paraId]);
    onReplyConsumed?.();
    // Solo al llegar un replyTo nuevo (p. ej. desde el popup en pantalla).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, replyTo?.paraId]);

  useEffect(() => {
    return () => {
      archivos.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buscarPropiedades = (valor) => {
    setBusquedaProp(valor);
    if (debouncePropRef.current) clearTimeout(debouncePropRef.current);
    const q = valor.trim();
    if (q.length < 2) {
      setResultadosProp([]);
      return;
    }
    debouncePropRef.current = setTimeout(async () => {
      setBuscandoProp(true);
      try {
        const { data } = await api.get("/propiedades", { params: { buscar: q } });
        setResultadosProp((data?.data || []).slice(0, 8));
      } catch {
        setResultadosProp([]);
      } finally {
        setBuscandoProp(false);
      }
    }, 350);
  };

  if (!mounted) return null;

  const pedirCerrar = () => {
    if (animOut) return;
    onClose?.();
  };

  const applyQuick = (qn) => {
    setCuerpo((prev) => (prev ? `${prev}\n${qn.text}` : qn.text));
    setPlantillaId(qn.id);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const agregarArchivos = (fileList) => {
    const nuevos = Array.from(fileList || []);
    if (!nuevos.length) return;
    setArchivos((prev) => {
      const disponibles = MAX_ARCHIVOS - prev.length;
      if (disponibles <= 0) {
        dialogs.alert(`Solo se permiten hasta ${MAX_ARCHIVOS} archivos`);
        return prev;
      }
      const aceptados = [];
      const rechazados = [];
      for (const f of nuevos.slice(0, disponibles)) {
        if (f.size > MAX_MB * 1024 * 1024) {
          rechazados.push(f.name);
          continue;
        }
        aceptados.push({
          file: f,
          preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null,
        });
      }
      if (rechazados.length) {
        dialogs.alert(`No se añadieron (superan ${MAX_MB} MB): ${rechazados.join(", ")}`);
      }
      return [...prev, ...aceptados];
    });
  };

  const quitarArchivo = (idx) => {
    setArchivos((prev) => {
      const item = prev[idx];
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const abrirMensaje = async (m) => {
    setSeleccionado(m);
    if (!m.leido_at && caja === "recibidos") {
      try {
        await api.patch(`/mensajes-internos/${m.id}/leido`);
        setMensajes((list) =>
          list.map((x) => (x.id === m.id ? { ...x, leido_at: new Date().toISOString() } : x))
        );
        setSeleccionado((prev) => (prev ? { ...prev, leido_at: new Date().toISOString() } : prev));
        refrescarNoLeidos();
      } catch {
        /* ok */
      }
    }
  };

  const responderA = (m) => {
    setTab("nuevo");
    setParaIds(m.de_usuario_id ? [m.de_usuario_id] : []);
    setCuerpo("");
    setPlantillaId(null);
    setSeleccionado(null);
    setEnviado(false);
    setPropiedadesSel(propsDeMensaje(m));
  };

  const toggleDestinatario = (id) => {
    setParaIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const marcarTodos = async () => {
    try {
      await api.patch("/mensajes-internos/leidos");
      await cargarBandeja();
      refrescarNoLeidos();
    } catch (e) {
      await dialogs.alert(e?.response?.data?.error || "No se pudieron marcar como leídos");
    }
  };

  const enviar = async () => {
    if (!paraIds.length) {
      await dialogs.alert("Elige al menos un destinatario");
      return;
    }
    if (!cuerpo.trim() && !archivos.length && !propiedadesSel.length) {
      await dialogs.alert("Escribe un mensaje, adjunta un archivo o vincula una propiedad");
      return;
    }
    setEnviando(true);
    try {
      const fd = new FormData();
      // Mismo mensaje para uno o varios destinatarios.
      fd.append("para_usuario_ids", JSON.stringify(paraIds));
      fd.append("cuerpo", cuerpo.trim());
      if (plantillaId) fd.append("plantilla_id", plantillaId);
      if (propiedadesSel.length) {
        fd.append("propiedad_ids", JSON.stringify(propiedadesSel.map((p) => p.id)));
      }
      archivos.forEach((a) => fd.append("adjuntos", a.file));
      const { data } = await postCaptacionMultipart("/mensajes-internos", fd);
      setEnviadoA(data?.enviados || paraIds.length);
      setEnviado(true);
      setCuerpo("");
      setPlantillaId(null);
      setParaIds([]);
      setFiltroDest("");
      setArchivos((prev) => {
        prev.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
        return [];
      });
      setPropiedadesSel([]);
      setBusquedaProp("");
      setResultadosProp([]);
      refrescarNoLeidos();
    } catch (e) {
      await dialogs.alert(e?.response?.data?.error || e?.message || "No se pudo enviar");
    } finally {
      setEnviando(false);
    }
  };

  const inpSt = {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 8,
    fontSize: 13,
    background: isDark ? "#111827" : "#f9fafb",
    border: `1.5px solid ${isDark ? "#374151" : "#e5e7eb"}`,
    color: isDark ? "#f9fafb" : "#111827",
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  };

  const labelSt = {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: t.textMuted,
    display: "block",
    marginBottom: 5,
  };

  const chipPropiedad = (p) => {
    const id = p.id || p.propiedad_id;
    return (
      <button
        key={id}
        type="button"
        onClick={() => id && setVistaPreviaId(id)}
        title="Abrir vista previa"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "5px 10px",
          borderRadius: 8,
          border: `1px solid ${ACCENT.border}`,
          background: ACCENT.soft,
          color: isDark ? ACCENT.text : ACCENT.from,
          fontSize: 12,
          fontWeight: 700,
          cursor: "pointer",
          fontFamily: "inherit",
          maxWidth: "100%",
        }}
      >
        <span>🏠</span>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {p.propiedad_referencia || p.referencia || "Propiedad"}
          {(p.propiedad_etiqueta || p.etiqueta) ? ` · ${p.propiedad_etiqueta || p.etiqueta}` : ""}
        </span>
      </button>
    );
  };

  const tabBtn = (id, label) => (
    <button
      key={id}
      type="button"
      onClick={() => {
        setTab(id);
        setSeleccionado(null);
        setEnviado(false);
      }}
      style={{
        flex: 1,
        padding: "9px 12px",
        borderRadius: 8,
        border: "none",
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: 13,
        fontWeight: 700,
        background: tab === id ? `linear-gradient(135deg, ${ACCENT.from}, ${ACCENT.to})` : "transparent",
        color: tab === id ? "#fff" : t.textMuted,
      }}
    >
      {label}
    </button>
  );

  const modal = (
    <div
      data-modal-form
      className={animOut ? "vc-msg-backdrop-out" : "vc-msg-backdrop-in"}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 600,
        fontFamily: "'DM Sans', sans-serif",
      }}
      onClick={pedirCerrar}
    >
      <div
        className={animOut ? "vc-msg-modal-out" : "vc-msg-modal-in"}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 580,
          maxWidth: "94vw",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          background: t.bgCard,
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.4), 0 0 48px rgba(20,184,166,0.12)",
          border: `1px solid ${t.border}`,
        }}
      >
        <div style={{ height: 3, flexShrink: 0, background: ACCENT.bar }} />

        <div style={{ padding: "18px 22px 0", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: `linear-gradient(135deg, ${ACCENT.from}, ${ACCENT.to})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                boxShadow: "0 4px 12px rgba(13,148,136,0.35)",
              }}
            >
              💬
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: t.textPrimary }}>Comunicación interna</h2>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: t.textMuted }}>
                Mensajes entre el equipo · tiempo real · no es soporte técnico
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={pedirCerrar}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: `1px solid ${t.border}`,
              background: isDark ? "#1f2937" : "#f3f4f6",
              cursor: "pointer",
              fontSize: 16,
              color: t.textMuted,
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: "14px 22px 0", flexShrink: 0 }}>
          <div
            style={{
              display: "flex",
              gap: 4,
              padding: 4,
              borderRadius: 10,
              background: isDark ? "rgba(255,255,255,0.04)" : "#f3f4f6",
            }}
          >
            {tabBtn("bandeja", "📥 Bandeja")}
            {tabBtn("nuevo", "✏️ Nuevo mensaje")}
          </div>
        </div>

        <div style={{ height: 1, flexShrink: 0, background: `linear-gradient(90deg, transparent, ${t.border}, transparent)`, marginTop: 14 }} />

        {tab === "nuevo" ? (
          enviado ? (
            <div style={{ padding: "36px 24px", textAlign: "center" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: t.textPrimary }}>Mensaje enviado</h3>
              <p style={{ margin: "8px 0 0", fontSize: 13, color: t.textMuted }}>
                {enviadoA > 1
                  ? `El mismo mensaje se ha enviado a ${enviadoA} compañeros.`
                  : "Tu compañero lo recibe al instante en pantalla."}
              </p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 20 }}>
                <button
                  type="button"
                  onClick={() => {
                    setEnviado(false);
                    setParaIds([]);
                  }}
                  style={{
                    padding: "10px 20px",
                    borderRadius: 10,
                    border: `1px solid ${t.border}`,
                    background: "transparent",
                    color: t.textPrimary,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  Otro mensaje
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTab("bandeja");
                    setCaja("enviados");
                    setEnviado(false);
                  }}
                  style={{
                    padding: "10px 20px",
                    borderRadius: 10,
                    border: "none",
                    background: `linear-gradient(135deg, ${ACCENT.from}, ${ACCENT.to})`,
                    color: "#fff",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  Ver enviados
                </button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ flex: 1, overflowY: "auto", padding: "16px 22px 8px", display: "flex", flexDirection: "column", gap: 14 }}>
                <div>
                  <label style={labelSt}>Para * (uno o varios usuarios)</label>
                  {paraIds.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                      {paraIds.map((id) => {
                        const u = destinatarios.find((d) => String(d.id) === String(id));
                        return (
                          <button
                            key={id}
                            type="button"
                            onClick={() => toggleDestinatario(id)}
                            title="Quitar"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "5px 10px",
                              borderRadius: 8,
                              border: `1px solid ${ACCENT.border}`,
                              background: ACCENT.soft,
                              color: isDark ? ACCENT.text : ACCENT.from,
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: "pointer",
                              fontFamily: "inherit",
                            }}
                          >
                            {u?.nombre || "Usuario"} ✕
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => setParaIds([])}
                        style={{
                          border: "none",
                          background: "transparent",
                          color: t.textMuted,
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        Limpiar
                      </button>
                    </div>
                  )}
                  <input
                    value={filtroDest}
                    onChange={(e) => setFiltroDest(e.target.value)}
                    placeholder="Buscar usuario por nombre o email…"
                    style={{ ...inpSt, marginBottom: 6 }}
                  />
                  <div
                    style={{
                      maxHeight: 160,
                      overflowY: "auto",
                      borderRadius: 8,
                      border: `1px solid ${t.border}`,
                      background: isDark ? "#111827" : "#f9fafb",
                    }}
                  >
                    {destinatarios
                      .filter((u) => {
                        const q = filtroDest.trim().toLowerCase();
                        if (!q) return true;
                        return (
                          String(u.nombre || "").toLowerCase().includes(q) ||
                          String(u.email || "").toLowerCase().includes(q)
                        );
                      })
                      .map((u) => {
                        const checked = paraIds.some((id) => String(id) === String(u.id));
                        return (
                          <label
                            key={u.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              padding: "8px 12px",
                              borderBottom: `1px solid ${t.border}`,
                              cursor: "pointer",
                              background: checked ? ACCENT.soft : "transparent",
                              fontFamily: "inherit",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleDestinatario(u.id)}
                              style={{ accentColor: ACCENT.from }}
                            />
                            <span style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: t.textPrimary }}>
                                {u.nombre}
                              </span>
                              {u.email && (
                                <span style={{ display: "block", fontSize: 11, color: t.textMuted }}>
                                  {u.email}
                                </span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    {destinatarios.length === 0 && (
                      <div style={{ padding: 12, fontSize: 12, color: t.textMuted }}>Cargando usuarios…</div>
                    )}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 11, color: t.textMuted }}>
                    {paraIds.length === 0
                      ? "Selecciona uno o varios. El mensaje es el mismo para todos."
                      : `${paraIds.length} seleccionado${paraIds.length === 1 ? "" : "s"} · mismo mensaje`}
                  </div>
                </div>

                <div>
                  <div style={{ ...labelSt, marginBottom: 8 }}>Mensajes rápidos</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {QUICK_MSGS.map((qn) => (
                      <button
                        key={qn.id}
                        type="button"
                        onClick={() => applyQuick(qn)}
                        style={{
                          padding: "6px 11px",
                          borderRadius: 6,
                          border: `1px solid ${ACCENT.border}`,
                          background: ACCENT.soft,
                          color: isDark ? ACCENT.text : ACCENT.from,
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        {qn.emoji} {qn.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label style={labelSt}>Vincular propiedades</label>
                  {propiedadesSel.length > 0 && (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                      {propiedadesSel.map((p) => (
                        <span key={p.id} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          {chipPropiedad(p)}
                          <button
                            type="button"
                            onClick={() => setPropiedadesSel((prev) => prev.filter((x) => x.id !== p.id))}
                            title="Quitar"
                            style={{
                              border: "none",
                              background: "transparent",
                              color: t.textMuted,
                              cursor: "pointer",
                              fontSize: 12,
                              fontFamily: "inherit",
                              padding: 0,
                            }}
                          >
                            ✕
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div style={{ position: "relative" }}>
                    <input
                      value={busquedaProp}
                      onChange={(e) => buscarPropiedades(e.target.value)}
                      placeholder="Buscar por referencia, dirección, propietario, contacto, teléfono, email…"
                      style={inpSt}
                    />
                    {(buscandoProp || resultadosProp.length > 0) && (
                      <div
                        style={{
                          position: "absolute",
                          left: 0,
                          right: 0,
                          top: "100%",
                          marginTop: 4,
                          zIndex: 5,
                          background: t.bgCard,
                          border: `1px solid ${t.border}`,
                          borderRadius: 10,
                          boxShadow: "0 12px 28px rgba(0,0,0,0.25)",
                          maxHeight: 220,
                          overflowY: "auto",
                        }}
                      >
                        {buscandoProp && (
                          <div style={{ padding: 10, fontSize: 12, color: t.textMuted }}>Buscando…</div>
                        )}
                        {!buscandoProp &&
                          resultadosProp.map((p) => {
                            const etiqueta = [p.direccion_completa, p.poblacion].filter(Boolean).join(" · ");
                            const ya = propiedadesSel.some((x) => x.id === p.id);
                            return (
                              <button
                                key={p.id}
                                type="button"
                                disabled={ya}
                                onClick={() => {
                                  setPropiedadesSel((prev) => {
                                    if (prev.some((x) => x.id === p.id)) return prev;
                                    return [...prev, { id: p.id, referencia: p.referencia, etiqueta }];
                                  });
                                  setBusquedaProp("");
                                  setResultadosProp([]);
                                }}
                                style={{
                                  display: "block",
                                  width: "100%",
                                  textAlign: "left",
                                  padding: "10px 12px",
                                  border: "none",
                                  borderBottom: `1px solid ${t.border}`,
                                  background: "transparent",
                                  cursor: ya ? "default" : "pointer",
                                  fontFamily: "inherit",
                                  opacity: ya ? 0.5 : 1,
                                }}
                              >
                                <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary }}>
                                  {p.referencia || "Sin ref."}
                                  {ya ? " · ya añadida" : ""}
                                </div>
                                <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
                                  {etiqueta || p.propietario || "—"}
                                </div>
                              </button>
                            );
                          })}
                        {!buscandoProp && resultadosProp.length === 0 && busquedaProp.trim().length >= 2 && (
                          <div style={{ padding: 10, fontSize: 12, color: t.textMuted }}>Sin resultados</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label style={labelSt}>Mensaje</label>
                  <textarea
                    ref={textareaRef}
                    value={cuerpo}
                    onChange={(e) => setCuerpo(e.target.value.slice(0, 5000))}
                    placeholder="Escribe tu mensaje o usa un botón rápido arriba…"
                    style={{ ...inpSt, resize: "vertical", minHeight: 100, fontFamily: "inherit" }}
                    autoFocus
                  />
                  <div style={{ textAlign: "right", fontSize: 10.5, color: t.textMuted, marginTop: 4, opacity: 0.6 }}>
                    {cuerpo.length} / 5.000
                  </div>
                </div>

                <div>
                  <label style={labelSt}>Adjuntos</label>
                  <input
                    ref={inputFileRef}
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      agregarArchivos(e.target.files);
                      e.target.value = "";
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => inputFileRef.current?.click()}
                    disabled={archivos.length >= MAX_ARCHIVOS}
                    style={{
                      padding: "8px 14px",
                      borderRadius: 8,
                      border: `1.5px dashed ${isDark ? "#374151" : "#cbd5e1"}`,
                      background: isDark ? "#111827" : "#f9fafb",
                      color: t.textMuted,
                      cursor: archivos.length >= MAX_ARCHIVOS ? "not-allowed" : "pointer",
                      fontFamily: "inherit",
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    📎 Añadir archivos
                  </button>
                  <p style={{ margin: "5px 0 0", fontSize: 11, color: t.textMuted }}>
                    Hasta {MAX_ARCHIVOS} archivos, {MAX_MB} MB cada uno
                  </p>
                  {archivos.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                      {archivos.map((a, idx) => (
                        <div
                          key={idx}
                          style={{
                            position: "relative",
                            width: 72,
                            height: 72,
                            borderRadius: 8,
                            overflow: "hidden",
                            border: `1px solid ${t.border}`,
                            background: isDark ? "#111827" : "#f1f5f9",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column",
                          }}
                          title={`${a.file.name} (${formatoTamano(a.file.size)})`}
                        >
                          {a.preview ? (
                            <img src={a.preview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            <>
                              <span style={{ fontSize: 22 }}>{iconoPorTipo(a.file)}</span>
                              <span
                                style={{
                                  fontSize: 9,
                                  color: t.textMuted,
                                  padding: "0 4px",
                                  textAlign: "center",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: "100%",
                                }}
                              >
                                {a.file.name}
                              </span>
                            </>
                          )}
                          <button
                            type="button"
                            onClick={() => quitarArchivo(idx)}
                            style={{
                              position: "absolute",
                              top: 2,
                              right: 2,
                              width: 18,
                              height: 18,
                              borderRadius: "50%",
                              border: "none",
                              background: "rgba(0,0,0,0.65)",
                              color: "#fff",
                              fontSize: 11,
                              cursor: "pointer",
                              padding: 0,
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div
                style={{
                  padding: "12px 22px 18px",
                  display: "flex",
                  gap: 10,
                  justifyContent: "flex-end",
                  borderTop: `1px solid ${t.border}`,
                  flexShrink: 0,
                }}
              >
                <button
                  type="button"
                  onClick={pedirCerrar}
                  style={{
                    padding: "10px 20px",
                    borderRadius: 10,
                    border: `1px solid ${t.border}`,
                    background: "transparent",
                    color: t.textPrimary,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={enviar}
                  disabled={enviando || !paraIds.length || (!cuerpo.trim() && !archivos.length && !propiedadesSel.length)}
                  style={{
                    padding: "10px 24px",
                    borderRadius: 10,
                    border: "none",
                    background:
                      enviando || !paraIds.length || (!cuerpo.trim() && !archivos.length && !propiedadesSel.length)
                        ? t.bgInput
                        : `linear-gradient(135deg, ${ACCENT.from}, ${ACCENT.to})`,
                    color:
                      enviando || !paraIds.length || (!cuerpo.trim() && !archivos.length && !propiedadesSel.length)
                        ? t.textMuted
                        : "#fff",
                    cursor:
                      enviando || !paraIds.length || (!cuerpo.trim() && !archivos.length && !propiedadesSel.length)
                        ? "not-allowed"
                        : "pointer",
                    fontFamily: "inherit",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  {enviando
                    ? "Enviando…"
                    : paraIds.length > 1
                      ? `Enviar a ${paraIds.length}`
                      : "Enviar mensaje"}
                </button>
              </div>
            </>
          )
        ) : (
          <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 320 }}>
            <div style={{ padding: "12px 22px 0", display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              {["recibidos", "enviados"].map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => {
                    setCaja(c);
                    setSeleccionado(null);
                  }}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 999,
                    border: `1px solid ${caja === c ? ACCENT.border : t.border}`,
                    background: caja === c ? ACCENT.soft : "transparent",
                    color: caja === c ? (isDark ? ACCENT.text : ACCENT.from) : t.textMuted,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  {c === "recibidos" ? "Recibidos" : "Enviados"}
                </button>
              ))}
              <div style={{ flex: 1 }} />
              {caja === "recibidos" && (
                <button
                  type="button"
                  onClick={marcarTodos}
                  style={{
                    padding: "5px 10px",
                    borderRadius: 6,
                    border: "none",
                    background: "transparent",
                    color: t.textMuted,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >
                  Marcar todos leídos
                </button>
              )}
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: "10px 16px 16px", display: "flex", gap: 10 }}>
              <div style={{ flex: seleccionado ? "0 0 46%" : "1 1 auto", overflowY: "auto", minWidth: 0 }}>
                {cargando ? (
                  <p style={{ textAlign: "center", color: t.textMuted, fontSize: 13, padding: 28 }}>Cargando…</p>
                ) : mensajes.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "36px 16px" }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
                    <p style={{ margin: 0, fontSize: 13, color: t.textMuted }}>
                      {caja === "recibidos" ? "No tienes mensajes recibidos" : "Aún no has enviado ninguno"}
                    </p>
                  </div>
                ) : (
                  mensajes.map((m, idx) => {
                    const noLeido = caja === "recibidos" && !m.leido_at;
                    const activo = seleccionado?.id === m.id;
                    const nombre = caja === "recibidos" ? m.de_nombre : m.para_nombre;
                    const propsMsg = propsDeMensaje(m);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        className="vc-msg-row-in"
                        onClick={() => abrirMensaje(m)}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "11px 12px",
                          marginBottom: 6,
                          borderRadius: 10,
                          border: `1px solid ${activo ? ACCENT.border : t.border}`,
                          background: activo ? ACCENT.soft : noLeido ? ACCENT.soft : "transparent",
                          cursor: "pointer",
                          fontFamily: "inherit",
                          animationDelay: `${Math.min(idx, 12) * 40}ms`,
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                          {noLeido && (
                            <span style={{ width: 7, height: 7, borderRadius: "50%", background: ACCENT.to, flexShrink: 0 }} />
                          )}
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: noLeido ? 800 : 600,
                              color: t.textPrimary,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {caja === "recibidos" ? "De: " : "Para: "}
                            {nombre || "—"}
                          </span>
                          <span style={{ marginLeft: "auto", fontSize: 10, color: t.textMuted, flexShrink: 0 }}>
                            {fmtFecha(m.created_at)}
                          </span>
                        </div>
                        {propsMsg.length > 0 && (
                          <div style={{ fontSize: 11, color: isDark ? ACCENT.text : ACCENT.from, marginBottom: 2, paddingLeft: noLeido ? 15 : 0 }}>
                            🏠 {propsMsg.map((p) => p.referencia).filter(Boolean).join(", ") || "Propiedad"}
                          </div>
                        )}
                        <div
                          style={{
                            fontSize: 12,
                            color: t.textMuted,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            paddingLeft: noLeido ? 15 : 0,
                          }}
                        >
                          {m.cuerpo || (m.adjuntos?.length ? `📎 ${m.adjuntos.length} adjunto(s)` : "(Sin texto)")}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {seleccionado && (
                <div
                  className="vc-msg-detail-in"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: 12,
                    border: `1px solid ${t.border}`,
                    padding: 14,
                    display: "flex",
                    flexDirection: "column",
                    background: isDark ? "rgba(255,255,255,0.03)" : "#fafafa",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: t.textPrimary }}>
                        {caja === "recibidos" ? seleccionado.de_nombre : seleccionado.para_nombre}
                      </div>
                      <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
                        {fmtFecha(seleccionado.created_at)}
                        {seleccionado.leido_at ? " · Leído" : caja === "recibidos" ? " · Nuevo" : ""}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSeleccionado(null)}
                      style={{ border: "none", background: "transparent", color: t.textMuted, cursor: "pointer" }}
                    >
                      ✕
                    </button>
                  </div>
                  {propsDeMensaje(seleccionado).length > 0 && (
                    <div style={{ marginBottom: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {propsDeMensaje(seleccionado).map((p) => chipPropiedad(p))}
                    </div>
                  )}
                  <div
                    style={{
                      flex: 1,
                      fontSize: 13.5,
                      lineHeight: 1.55,
                      color: t.textPrimary,
                      whiteSpace: "pre-wrap",
                      overflowY: "auto",
                    }}
                  >
                    {seleccionado.cuerpo || "(Sin texto)"}
                  </div>
                  {!!seleccionado.adjuntos?.length && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                      {seleccionado.adjuntos.map((a) => (
                        <a
                          key={a.id}
                          href={a.url}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: isDark ? ACCENT.text : ACCENT.from,
                            textDecoration: "none",
                            padding: "4px 8px",
                            borderRadius: 6,
                            border: `1px solid ${ACCENT.border}`,
                            background: ACCENT.soft,
                          }}
                        >
                          📎 {a.nombre_original || "Adjunto"}
                        </a>
                      ))}
                    </div>
                  )}
                  {caja === "recibidos" && (
                    <button
                      type="button"
                      onClick={() => responderA(seleccionado)}
                      style={{
                        marginTop: 12,
                        padding: "9px 14px",
                        borderRadius: 8,
                        border: "none",
                        background: `linear-gradient(135deg, ${ACCENT.from}, ${ACCENT.to})`,
                        color: "#fff",
                        cursor: "pointer",
                        fontFamily: "inherit",
                        fontSize: 12,
                        fontWeight: 700,
                        alignSelf: "flex-end",
                      }}
                    >
                      Responder
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(
    <>
      {modal}
      {vistaPreviaId && (
        <FichaResumenPropiedad propiedadId={vistaPreviaId} onClose={() => setVistaPreviaId(null)} />
      )}
    </>,
    document.body
  );
}
